import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { CampManagerAccessService } from './camp-manager-access.service';
import { CAMP_MANAGER_CONFIG } from '../config/camp-manager.config';

@Injectable({
  providedIn: 'root',
})
export class CanAccessCampManagerGuard implements CanActivate, CanActivateChild {
  constructor(
    private authService: AuthService,
    private campManagerAccessService: CampManagerAccessService,
    private router: Router
  ) {}

  /**
   * Check if the current user has camp manager access
   * @returns boolean - True if user is approved, false otherwise
   */
  private checkCampManagerAccess(): boolean {
    const currentUser = this.authService.currentUserValue;

    console.log('CanAccessCampManagerGuard: Checking camp manager access', { currentUser: currentUser?.email });

    // First check if user is logged in
    if (!currentUser || !currentUser.email) {
      console.warn('CanAccessCampManagerGuard: No authenticated user found');
      this.router.navigate([CAMP_MANAGER_CONFIG.loginRedirectPath]);
      return false;
    }

    // Check if user's email is expired
    if (currentUser.expiredAt && currentUser.expiredAt * 1000 < new Date().getTime() + 1) {
      console.warn('CanAccessCampManagerGuard: User session expired');
      this.router.navigate([CAMP_MANAGER_CONFIG.loginRedirectPath]);
      return false;
    }

    // Check if user is approved for camp manager access
    const isApproved = this.campManagerAccessService.isCampManagerApproved(currentUser.email.toLowerCase());

    if (!isApproved) {
      console.warn(
        `CanAccessCampManagerGuard: User email '${currentUser.email}' is not approved for camp manager access`
      );
      this.router.navigate([CAMP_MANAGER_CONFIG.unauthorizedRedirectPath]);
      return false;
    }

    console.log(`CanAccessCampManagerGuard: User email '${currentUser.email}' is approved for camp manager access`);
    return true;
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.checkCampManagerAccess();
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.checkCampManagerAccess();
  }
}
